import { useState, useEffect, useRef } from "react";
import {
  RECOGNITION_MODEL_ID,
  RECOGNITION_DEFAULT_THRESHOLD,
} from "@ndp61/cms-shared";
import type { EmbeddingBackendInfo, MarkerEmbeddingsFile } from "@ndp61/cms-shared";
import { GpsCapture } from "./screens/GpsCapture";
import { MetadataEditor } from "./screens/MetadataEditor";
import { PhotoCapture } from "./screens/PhotoCapture";
import { EmbeddingCompute } from "./screens/EmbeddingCompute";
import { RecognitionTest } from "./screens/RecognitionTest";
import type { VerificationResult } from "./screens/RecognitionTest";
import { ZipExport } from "./screens/ZipExport";
import type { GpsResult } from "./lib/gps";
import { FIELD_CAPTURE_DEFAULT_HOLD_MS } from "./constants";

const STEPS = ["Meta", "GPS", "Photos", "Compute", "Verify", "Export"] as const;
type Step = (typeof STEPS)[number];

interface AppState {
  poiId: string;
  threshold: number;
  holdMs: number;
  gps: GpsResult | null;
  photos: { hero: Blob | null; mission: Blob | null; markers: Blob[] };
  embeddings: MarkerEmbeddingsFile | null;
  backendInfo: EmbeddingBackendInfo | null;
  verificationResult: VerificationResult | null;
  notes: string;
}

// ── Session persistence ───────────────────────────────────────────────────────

const IDB_DB = "ndp61-fc";
const IDB_STORE = "blobs";
const IDB_VERSION = 1;
const SS_KEY = "ndp61-fc-state";

function openIdb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_DB, IDB_VERSION);
    req.onupgradeneeded = () => req.result.createObjectStore(IDB_STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbPut(key: string, value: Blob): Promise<void> {
  const db = await openIdb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, "readwrite");
    tx.objectStore(IDB_STORE).put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function idbGet(key: string): Promise<Blob | undefined> {
  const db = await openIdb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, "readonly");
    const req = tx.objectStore(IDB_STORE).get(key);
    req.onsuccess = () => resolve(req.result as Blob | undefined);
    req.onerror = () => reject(req.error);
  });
}

async function idbClear(): Promise<void> {
  const db = await openIdb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, "readwrite");
    tx.objectStore(IDB_STORE).clear();
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

interface PersistedSession {
  stepIndex: number;
  poiId: string;
  threshold: number;
  holdMs: number;
  gps: GpsResult | null;
  embeddings: MarkerEmbeddingsFile | null;
  backendInfo: EmbeddingBackendInfo | null;
  verificationResult: VerificationResult | null;
  notes: string;
  markerCount: number;
  hasHero: boolean;
  hasMission: boolean;
}

function readPersistedSession(): PersistedSession | null {
  try {
    const raw = sessionStorage.getItem(SS_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as PersistedSession;
  } catch {
    return null;
  }
}

function saveSession(stepIndex: number, state: AppState): void {
  const session: PersistedSession = {
    stepIndex,
    poiId: state.poiId,
    threshold: state.threshold,
    holdMs: state.holdMs,
    gps: state.gps,
    embeddings: state.embeddings,
    backendInfo: state.backendInfo,
    verificationResult: state.verificationResult,
    notes: state.notes,
    markerCount: state.photos.markers.length,
    hasHero: state.photos.hero != null,
    hasMission: state.photos.mission != null,
  };
  try {
    sessionStorage.setItem(SS_KEY, JSON.stringify(session));
  } catch {
    // quota exceeded — non-fatal
  }
}

const INITIAL_APP_STATE: AppState = {
  poiId: "",
  threshold: RECOGNITION_DEFAULT_THRESHOLD,
  holdMs: FIELD_CAPTURE_DEFAULT_HOLD_MS,
  gps: null,
  photos: { hero: null, mission: null, markers: [] },
  embeddings: null,
  backendInfo: null,
  verificationResult: null,
  notes: "",
};

// ── App ───────────────────────────────────────────────────────────────────────

export function App() {
  // Lazy initialisers read scalar state from sessionStorage synchronously so
  // the first render already shows the correct step — no flicker.
  const [stepIndex, setStepIndex] = useState<number>(() => {
    const stored = readPersistedSession()?.stepIndex;
    if (stored === undefined) return 0;
    return Number.isFinite(stored) && stored >= 0 && stored < STEPS.length ? stored : 0;
  });

  const [state, setState] = useState<AppState>(() => {
    const session = readPersistedSession();
    if (!session || (!session.poiId && session.stepIndex === 0)) return INITIAL_APP_STATE;
    return {
      poiId: session.poiId,
      threshold: session.threshold,
      holdMs: session.holdMs,
      gps: session.gps,
      // Photos are blobs — loaded from IndexedDB asynchronously below.
      photos: { hero: null, mission: null, markers: [] },
      embeddings: session.embeddings,
      backendInfo: session.backendInfo,
      verificationResult: session.verificationResult,
      notes: session.notes,
    };
  });

  const [resuming, setResuming] = useState(false);

  // Gate saves until the IDB photo-restore pass has completed, so that
  // markerCount / hasHero / hasMission in sessionStorage remain accurate
  // even if the tab is evicted again during the restore itself.
  const photosLoadedRef = useRef(false);

  // Restore photo blobs from IndexedDB on mount.
  // Scalar state is already loaded via the useState lazy initialisers above.
  useEffect(() => {
    const session = readPersistedSession();

    if (!session || (!session.poiId && session.stepIndex === 0)) {
      // Nothing meaningful to restore.
      photosLoadedRef.current = true;
      return;
    }

    if (!session.hasHero && !session.hasMission && session.markerCount === 0) {
      // Scalars restored, no photos in IDB to fetch.
      photosLoadedRef.current = true;
      setResuming(true);
      return;
    }

    Promise.all([
      session.hasHero ? idbGet("hero") : Promise.resolve(undefined),
      session.hasMission ? idbGet("mission") : Promise.resolve(undefined),
      ...Array.from({ length: session.markerCount }, (_, i) => idbGet(`marker-${i}`)),
    ])
      .then(([heroBlob, missionBlob, ...markerBlobs]) => {
        photosLoadedRef.current = true;
        setState((s) => ({
          ...s,
          photos: {
            hero: heroBlob ?? null,
            mission: missionBlob ?? null,
            markers: markerBlobs.filter((b): b is Blob => b != null),
          },
        }));
        setResuming(true);
      })
      .catch(() => {
        // IDB unavailable — scalars already restored, photos lost.
        photosLoadedRef.current = true;
        setResuming(true);
      });
  }, []); // mount only — eslint-disable-line react-hooks/exhaustive-deps

  // Persist session state whenever stepIndex or state changes.
  // Gated until photos are loaded so markerCount/hasHero/hasMission stay accurate.
  useEffect(() => {
    if (!photosLoadedRef.current) return;
    saveSession(stepIndex, state);
  }, [stepIndex, state]);

  // Auto-dismiss the resuming banner.
  useEffect(() => {
    if (!resuming) return;
    const t = setTimeout(() => setResuming(false), 2000);
    return () => clearTimeout(t);
  }, [resuming]);

  const currentStep: Step = STEPS[stepIndex];
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === STEPS.length - 1;

  // Each step's own primary button (Continue / Capture / Proceed / …) advances
  // the flow on success. The bottom-bar Next is a manual override and must not
  // let users skip past unmet prerequisites.
  const nextBlocked =
    isLast ||
    currentStep === "Photos" ||
    (currentStep === "Meta" && state.poiId === "") ||
    (currentStep === "GPS" && state.gps === null) ||
    (currentStep === "Compute" && state.embeddings === null);

  function goBack() {
    setStepIndex((i) => Math.max(0, i - 1));
  }

  function goNext() {
    setStepIndex((i) => Math.min(STEPS.length - 1, i + 1));
  }

  function goToStep(step: Step) {
    const idx = STEPS.indexOf(step);
    if (idx >= 0) setStepIndex(idx);
  }

  return (
    <main style={styles.shell}>
      <header style={styles.header}>
        <h1 style={styles.title}>NDP61 Field Capture</h1>
        <p style={styles.modelTag}>{RECOGNITION_MODEL_ID}</p>
        <StepBar steps={STEPS} currentIndex={stepIndex} />
      </header>

      {resuming && (
        <div style={styles.resumeBanner} role="status" aria-live="polite">
          Resuming session…
        </div>
      )}

      <section style={styles.content} aria-live="polite">
        {currentStep === "Meta" && (
          <MetadataEditor
            onSubmit={({ poiId, threshold, holdMs }) => {
              setState((s) => ({ ...s, poiId, threshold, holdMs }));
              goNext();
            }}
          />
        )}

        {currentStep === "GPS" && (
          <GpsCapture
            captured={state.gps}
            onCapture={(result) => {
              setState((s) => ({ ...s, gps: result }));
              goNext();
            }}
          />
        )}

        {currentStep === "Photos" && (
          <PhotoCapture
            initialPhotos={state.photos}
            onCapture={(photos) => {
              // Persist blobs to IndexedDB so they survive a forced reload.
              if (photos.hero) idbPut("hero", photos.hero).catch(() => {});
              if (photos.mission) idbPut("mission", photos.mission).catch(() => {});
              photos.markers.forEach((blob, i) => {
                idbPut(`marker-${i}`, blob).catch(() => {});
              });
              setState((s) => ({ ...s, photos }));
              goNext();
            }}
          />
        )}

        {currentStep === "Compute" && (
          <EmbeddingCompute
            photos={state.photos.markers}
            onComplete={({ embeddings, backendInfo }) => {
              setState((s) => ({ ...s, embeddings, backendInfo }));
              goNext();
            }}
          />
        )}

        {currentStep === "Verify" && state.embeddings && (
          <RecognitionTest
            embeddings={state.embeddings}
            threshold={state.threshold}
            holdMs={state.holdMs}
            onComplete={(result) => {
              setState((s) => ({ ...s, verificationResult: result }));
              goNext();
            }}
            onGoBack={(target) => {
              if (target === "settings") {
                goToStep("Meta");
              } else {
                goToStep("Photos");
              }
            }}
          />
        )}

        {currentStep === "Verify" && !state.embeddings && (
          <div style={stepStyles.container}>
            <h2 style={stepStyles.heading}>Step 5 — Verify</h2>
            <p style={stepStyles.error}>
              Embeddings not yet computed. Go back to the Compute step.
            </p>
          </div>
        )}

        {currentStep === "Export" && state.gps && state.embeddings && (
          <ZipExport
            zipInput={{
              poiId: state.poiId,
              gps: state.gps,
              embeddings: state.embeddings,
              photos: state.photos.markers,
              heroPhoto: state.photos.hero ?? undefined,
              missionPhoto: state.photos.mission ?? undefined,
              threshold: state.threshold,
              holdMs: state.holdMs,
              verificationResult: state.verificationResult ?? undefined,
              backendInfo: state.backendInfo ?? undefined,
            }}
            verificationResult={state.verificationResult}
            notes={state.notes}
            onNotesChange={(notes) => setState((s) => ({ ...s, notes }))}
            onDone={() => {
              // Clear persisted blobs; save effect will overwrite scalars with
              // the blank state so the next load sees nothing to restore.
              idbClear().catch(() => {});
              setStepIndex(0);
              setState(INITIAL_APP_STATE);
            }}
          />
        )}

        {currentStep === "Export" && (!state.gps || !state.embeddings) && (
          <div style={stepStyles.container}>
            <h2 style={stepStyles.heading}>Step 6 — Export</h2>
            <p style={stepStyles.error}>
              Complete all previous steps before exporting.
            </p>
          </div>
        )}
      </section>

      <nav style={styles.nav}>
        <button
          style={{ ...styles.navBtn, ...(isFirst ? styles.navBtnDisabled : {}) }}
          onClick={goBack}
          disabled={isFirst}
          aria-label="Previous step"
        >
          Back
        </button>
        <span style={styles.stepLabel}>
          {stepIndex + 1} / {STEPS.length}
        </span>
        <button
          style={{ ...styles.navBtn, ...(nextBlocked ? styles.navBtnDisabled : {}) }}
          onClick={goNext}
          disabled={nextBlocked}
          aria-label="Next step"
        >
          Next
        </button>
      </nav>
    </main>
  );
}

function StepBar({
  steps,
  currentIndex,
}: {
  steps: readonly Step[];
  currentIndex: number;
}) {
  return (
    <ol style={stepBarStyles.list} aria-label="Capture steps">
      {steps.map((label, i) => {
        const done = i < currentIndex;
        const active = i === currentIndex;
        const dotColor = done ? "#2d7a2d" : active ? "#c8102e" : "#d0d0d0";
        return (
          <li
            key={label}
            style={stepBarStyles.item}
            aria-current={active ? "step" : undefined}
          >
            <span
              style={{ ...stepBarStyles.dot, background: dotColor }}
              aria-hidden="true"
            />
            <span
              style={{
                ...stepBarStyles.label,
                color: active ? "#c8102e" : done ? "#2d7a2d" : "#6b6b6b",
                fontWeight: active ? 700 : 400,
              }}
            >
              {label}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

// -- Inline styles ------------------------------------------------------------

const styles: Record<string, React.CSSProperties> = {
  shell: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    maxWidth: 480,
    margin: "0 auto",
    background: "#fff",
  },
  header: {
    padding: "16px 16px 0",
    borderBottom: "1px solid #e0e0e0",
  },
  title: {
    fontSize: 18,
    fontWeight: 700,
    marginBottom: 4,
    color: "#c8102e",
  },
  modelTag: {
    fontSize: 11,
    color: "#999",
    marginBottom: 8,
    fontFamily: "monospace",
  },
  resumeBanner: {
    padding: "8px 16px",
    background: "#e8f4fd",
    color: "#1a5276",
    fontSize: 13,
    fontWeight: 500,
    borderBottom: "1px solid #aed6f1",
    textAlign: "center",
  },
  content: {
    flex: 1,
    overflowY: "auto",
    padding: "16px",
  },
  nav: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 16px",
    borderTop: "1px solid #e0e0e0",
  },
  navBtn: {
    minWidth: 80,
    minHeight: 44,
    padding: "0 20px",
    borderRadius: 8,
    border: "1px solid #c8102e",
    background: "#c8102e",
    color: "#fff",
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
  },
  navBtnDisabled: {
    background: "#e0e0e0",
    border: "1px solid #e0e0e0",
    color: "#999",
    cursor: "not-allowed",
  },
  stepLabel: {
    fontSize: 14,
    color: "#6b6b6b",
  },
};

const stepBarStyles: Record<string, React.CSSProperties> = {
  list: {
    display: "flex",
    listStyle: "none",
    gap: 4,
    paddingBottom: 12,
    overflowX: "auto",
    margin: 0,
    padding: "0 0 12px 0",
  },
  item: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 4,
    flex: 1,
    minWidth: 40,
  },
  dot: {
    width: 10,
    height: 10,
    borderRadius: "50%",
    background: "#d0d0d0",
  },
  label: {
    fontSize: 10,
    color: "#6b6b6b",
    whiteSpace: "nowrap",
  },
};

const stepStyles: Record<string, React.CSSProperties> = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  heading: {
    fontSize: 17,
    fontWeight: 700,
    color: "#111",
  },
  error: {
    fontSize: 13,
    color: "#c0392b",
    background: "#fdf0ef",
    padding: "8px 12px",
    borderRadius: 8,
  },
};
